import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import { SettingsService } from '../services/settings.service';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { User } from 'web-common/src/app/user/user';
import { NotificationsService } from 'web-common/src/app/notifications.service';
import { FilterStylesPipe } from './admin-settings.styles.pipe';
import { SettingsModel } from '../datamodel/settings.datamodel';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import {ExampleModalComponent} from './admin-settings-modal/example-modal.component';

@Component({
  selector: 'app-admin-settings',
  templateUrl: './admin-settings.component.html',
  providers: [FilterStylesPipe]
})
export class AdminSettingsComponent implements OnInit {
  adminUser: User;
  allOutputStyles: any; // All styles to search from
  styleList = []; // Bound to inputs via ngModel
  defaultStylesList: any; // The dropdown lists (Dynamic)
  defaultStylesReset: any;
  settings: SettingsModel;
  focusedIndex: number;
  invalidURL = false;
  invalidAdditionalInfoLabel = false;
  invalidAnswer = false;
  copyleaksPilotEnded = false;
  get hideRequiredFields() { return !(this.invalidAdditionalInfoLabel || this.invalidAnswer); };
  modalRef: BsModalRef;
  additionalProfileAnswersList = '';
  @ViewChild('toasterCopiedWayfless', { static: true }) toasterCopiedWayfless: TemplateRef<any>;

  constructor(
    private route: ActivatedRoute,
    private settingsService: SettingsService,
    private notifications: NotificationsService,
    private modalService: BsModalService,
    private filterStyles: FilterStylesPipe
  ) {}

  private getStyles() {
    this.settingsService.getOutputStyles().subscribe(this.extractStyles, this.showError);
  }

  private getWayflessUrl() {
    this.settingsService.getWayflessUrl(this.adminUser.discountGroup.id).subscribe(
      url => this.settings.wayflessURL = url,
      error => this.settings.wayflessURL = '');
  }

  private extractStyles = res => {
    const defaultStyleIDs = this.adminUser.discountGroup['defaultCitationStyles'];
    const self = this;
    this.allOutputStyles = res.data.styles.all;
    this.allOutputStyles.forEach(style => {
      let str = '';
      if (style.userId === self.adminUser.id) {
        str = 'Mine';
      }
      if (style.styleType === 'csl') {
        if (str.length > 0) {
          str = str + ', CSL';
        } else {
          str = 'CSL';
        }
      }
      if (style.isInstitutional) {
        if (str.length > 0) {
          str = str + ', Institutional';
        } else {
          str = 'Institutional';
        }
      }
      style.ownership = str.length ? '(' + str + ')' : '';
    });
    this.defaultStylesList = []; // Empty the list before rebuilding
    defaultStyleIDs.forEach(function(styleID, i) {
      if (res.data.styles.all.findIndex(el => el.id == styleID) > -1) {
        self.defaultStylesList.push(res.data.styles.all.find(el => el.id == styleID));
        self.styleList.push(self.defaultStylesList[i].name);
      } else {
        self.defaultStylesList.push({name : '', id : ''});
        self.styleList.push('');
      }
    });
    this.defaultStylesReset = Object.assign([], this.defaultStylesList); // Getting a clean list to bind the form elements to
  }

  private saveSuccess = res => {
    this.notifications.success('Settings Saved Successfully');
    this.getStyles();
  }

  private showError = error => {
    this.notifications.error('An error has occurred. Please try again', 'Error');
  }

  private resetTypeaheadList() {
    this.defaultStylesList = Object.assign([], this.defaultStylesReset);
  }

  public resetFocus() {
    this.focusedIndex = -1;
    this.resetTypeaheadList();
  }

  ngOnInit() {
    this.adminUser = this.route.parent.snapshot.data['user'] as User;

    // Initialize Settings using SettingsModel
    this.settings = new SettingsModel(
      this.adminUser.discountGroup.defaultCitationStyles,
      (this.adminUser.discountGroup.linkResolver ? this.adminUser.discountGroup.linkResolver.name : ''),
      (this.adminUser.discountGroup.linkResolver ? this.adminUser.discountGroup.linkResolver.url : ''),
      this.adminUser.discountGroup.sharingProfile,
      this.adminUser.discountGroup.sponsorText,
      this.adminUser.discountGroup.sponsorURL,
      this.adminUser.discountGroup.linkResolver,
      this.adminUser.discountGroup.additionalProfileInformation ? this.adminUser.discountGroup.additionalProfileInformation.label : '',
      this.adminUser.discountGroup.additionalProfileInformation ?
        this.adminUser.discountGroup.additionalProfileInformation.answers : [],
      this.adminUser.discountGroup.profileCreationMessage
    );
    this.additionalProfileAnswersList = this.settings.additionalProfileInformationAnswers.join('\n');
    this.getStyles();
    this.getWayflessUrl();
    this.settingsService.copyleaksPilotWasEnded().subscribe(wasEnded => this.copyleaksPilotEnded = wasEnded);
  }
  // Event Handlers

  updateSettings(e) {
    this.settings.additionalProfileInformationAnswers = this.getAnswers();
    if (this.validateAdditionalProfileInformation()) {
      this.settingsService.saveSettings(this.settings).subscribe(this.saveSuccess, this.showError);
    }
  }

  public validateAdditionalProfileInformation() : boolean {
    let validateAnswers = this.validateAdditionalProfileInformationAnswers();
    let validateLabel = this.validateAdditionalProfileInformationEmptyLabel();
    return (validateAnswers && validateLabel);
  }

  private getAnswers() {
    let rawAnswers = this.additionalProfileAnswersList.split('\n');
    return rawAnswers.filter(answer => answer.toString().trim().length !== 0);
  }

  private validateAdditionalProfileInformationEmptyLabel(): boolean {
    let answersNotEmpty = this.settings.additionalProfileInformationAnswers.length > 0;
    let nameEmpty = this.settings.additionalProfileInformationName.trim().length === 0;
    this.invalidAdditionalInfoLabel = false;
    if (answersNotEmpty && nameEmpty) {
      this.invalidAdditionalInfoLabel = true;
      return false;
    }
    return true;
  }

  private validateAdditionalProfileInformationAnswers(): boolean {
    this.invalidAnswer = false;
    if (this.settings.additionalProfileInformationName.length !== 0) {
      let lines = this.getAnswers().length;
      if (lines <= 1) {
        this.invalidAnswer = true;
        return false;
      }
    }
    return true;
  }

  public openExampleModal() {
    const modalConfig: ModalOptions = {
      class: 'modal-md'
    };

    this.modalRef = this.modalService.show(ExampleModalComponent, modalConfig);
  }

  private generateHTMLString(node): string {
    return node.nativeElement.innerHTML;
  }

  public copyWayfless() {
    const input = document.getElementById('wayfless-input') as HTMLInputElement;
    input.focus();
    input.select();
    document.execCommand('copy');
    this.notifications.success(this.generateHTMLString(this.toasterCopiedWayfless));
  }


  selectStyle(style: any, i: number) {
    this.styleList[i] = style.name;
    this.settings.defaultCitationStyles[i] = style.id;
    this.resetTypeaheadList();
  }

  onTypeaheadFocus(index) {
    this.focusedIndex = index;
  }

  onTypeaheadBlur() {
    const self = this;
    setTimeout(function() {
      self.resetFocus();
    }, 250);
  }

  onInputKeyUp(e) {
    this.defaultStylesList = this.filterStyles.transform(this.allOutputStyles, e.target.value);
  }

}
